"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import Link from "next/link";
import { CompareBars, type BarSeries } from "@/components/compare-bars";
import { IdealSpecsBanner } from "@/components/ideal-specs-banner";
import { toSlug } from "@/lib/utils";
import type { IdealCpu, IdealGpu, LaptopInformation } from "@/lib/types";
import { appNamesFromAnswers, bestBy, buildIdealTargets, closestToTarget, deriveStrengthsWeaknesses, deriveVerdictLabel, generateSummaryParagraph, getCompany, getCompareColor, getDisplayName, getId, getImage, getMatchScore, getPrice, type IdealTargets, matchesTarget, normaliseToRadarScores, oneLineVerdict } from "@/lib/comparison";
import type { Answer } from "@/lib/match-explanations";

// The side-by-side breakdown on /compare. Laptops reach it two ways: ticked on
// the results page, where the recommender has scored them against the user's
// answers, or picked by hand on /compare, where there are no answers and no
// match scores. Every personalised element (ideal-specs banner, match score,
// "best for you" reasons) only renders when that context exists; without it
// the spec table falls back to highlighting the objectively strongest option.

// ── Icons ──────────────────────────────────────────────────────────────────────

function IconCheck() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg viewBox="0 0 16 16" fill="currentColor" className="size-3 shrink-0"><path fillRule="evenodd" d="M13.78 4.22a.75.75 0 0 1 0 1.06l-7 7a.75.75 0 0 1-1.06 0l-3.5-3.5a.75.75 0 1 1 1.06-1.06L6.25 10.69l6.47-6.47a.75.75 0 0 1 1.06 0Z" clipRule="evenodd" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function IconWarning() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg viewBox="0 0 16 16" fill="currentColor" className="size-3 shrink-0"><path d="M8 1.5a.9.9 0 0 1 .78.45l6.1 10.55a.9.9 0 0 1-.78 1.35H1.9a.9.9 0 0 1-.78-1.35L7.22 1.95A.9.9 0 0 1 8 1.5Zm0 4a.75.75 0 0 0-.75.75v2.5a.75.75 0 0 0 1.5 0v-2.5A.75.75 0 0 0 8 5.5Zm0 5a.9.9 0 1 0 0 1.8.9.9 0 0 0 0-1.8Z" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function IconStar() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg viewBox="0 0 16 16" fill="currentColor" className="size-3 shrink-0"><path d="M7.144 1.33A.996.996 0 0 1 8 .8c.361 0 .695.197.856.53l1.55 3.143 3.468.503c.72.104 1.007.988.487 1.494l-2.51 2.447.593 3.454c.123.718-.63 1.264-1.274.926L8 11.356l-3.17 1.667c-.643.338-1.397-.208-1.274-.926l.593-3.454-2.51-2.447c-.52-.506-.233-1.39.487-1.494l3.468-.503L7.144 1.33Z" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

// ── Formatting helpers ──────────────────────────────────────────────────────────

const AMBER = "oklch(0.62 0.16 55)";
function formatStorage(gb: number): string {
  if (!gb) return "—";
  return gb >= 1000 ? `${gb / 1000}TB` : `${gb}GB`;
}
function priceSymbol(country: string): string {
  return country === "us" ? "$" : "£";
}

// ── Spec table definition ───────────────────────────────────────────────────────

type SpecRow = {
  label: string;
  value: (item: LaptopInformation, sym: string) => string;
  // Returns the id(s) of the laptop(s) closest to the user's ideal for this spec.
  winnerIds: (items: LaptopInformation[], ideal: IdealTargets) => string[];
  // Tooltip shown on the highlighted (best-for-you) option.
  reason: (ideal: IdealTargets, sym: string, apps: string) => string;
};
const SPEC_ROWS: SpecRow[] = [{
  label: "Price",
  value: (it, sym) => getPrice(it) > 0 ? `${sym}${getPrice(it).toLocaleString()}` : "—",
  winnerIds: items => bestBy(items, it => getPrice(it) || null, "low"),
  reason: (ideal, sym) => ideal.budget != null ? `Lowest price here, and comfortably within your ${sym}${ideal.budget.toLocaleString()} budget.` : `The lowest price of the laptops you're comparing.`
}, {
  label: "Weight",
  value: it => it.specs?.weight_kg != null ? `${it.specs.weight_kg} kg` : "—",
  winnerIds: items => bestBy(items, it => it.specs?.weight_kg, "low"),
  reason: () => `The lightest to carry around campus all day.`
}, {
  label: "Screen size",
  value: it => it.specs?.display?.inches ? `${it.specs.display.inches}"` : "—",
  winnerIds: (items, ideal) => closestToTarget(items, it => it.specs?.display?.inches, ideal.screenInches),
  reason: ideal => `Closest to your preferred ${ideal.screenInches}" screen size.`
}, {
  label: "Memory (RAM)",
  value: it => it.specs?.ram?.size_gb ? `${it.specs.ram.size_gb}GB` : "—",
  winnerIds: (items, ideal) => ideal.ramGb != null ? closestToTarget(items, it => it.specs?.ram?.size_gb, ideal.ramGb, {
    preferAtLeast: true
  }) : bestBy(items, it => it.specs?.ram?.size_gb, "high"),
  reason: (ideal, _sym, apps) => ideal.ramGb != null ? `${ideal.ramGb}GB is what we recommend for ${apps}, and this meets it without paying for memory you won't use.` : `The most memory here, so it copes best with lots of apps and tabs open at once.`
}, {
  label: "Storage",
  value: it => formatStorage(it.specs?.storage?.size_gb),
  winnerIds: (items, ideal) => ideal.storageGb != null ? closestToTarget(items, it => it.specs?.storage?.size_gb, ideal.storageGb, {
    preferAtLeast: true
  }) : bestBy(items, it => it.specs?.storage?.size_gb, "high"),
  reason: (ideal, _sym, apps) => ideal.storageGb != null ? `${formatStorage(ideal.storageGb)} is enough room for ${apps} and your files, without overpaying.` : `The most room for apps, files and media.`
}, {
  label: "Processor (CPU)",
  value: it => it.cpu?.name ?? "—",
  winnerIds: (items, ideal) => ideal.perfSingle != null ? closestToTarget(items, it => it.cpu?.cinebench_2024, ideal.perfSingle) : bestBy(items, it => it.cpu?.cinebench_2024, "high"),
  reason: (ideal, _sym, apps) => ideal.perfSingle == null ? `The fastest processor of the laptops you're comparing.` : ideal.idealCpuName ? `Matches the power of the ${ideal.idealCpuName} we recommend for ${apps}: enough for years, without being overkill.` : `The closest match to the processing power ${apps} needs.`
}, {
  label: "Graphics (GPU)",
  value: it => it.gpu?.name ?? "—",
  winnerIds: (items, ideal) => ideal.gpuGaming != null ? closestToTarget(items, it => it.gpu?.three_d_mark_time_spy_g, ideal.gpuGaming) : bestBy(items, it => it.gpu?.three_d_mark_time_spy_g, "high"),
  reason: (ideal, _sym, apps) => ideal.gpuGaming == null ? `The strongest graphics of the laptops you're comparing.` : ideal.idealGpuName ? `Matches the graphics power of the ${ideal.idealGpuName} we recommend for ${apps}.` : `The closest match to the graphics power ${apps} needs.`
}, {
  label: "Battery & efficiency",
  value: it => {
    const s = it.scores?.battery_life ?? it.cpu?.review_power_efficiency;
    return s != null ? `${Math.round(s)}/100` : "—";
  },
  winnerIds: (items, ideal) => ideal.batteryScore != null ? closestToTarget(items, it => it.scores?.battery_life ?? it.cpu?.review_power_efficiency, ideal.batteryScore, {
    preferAtLeast: true
  }) : bestBy(items, it => it.scores?.battery_life ?? it.cpu?.review_power_efficiency, "high"),
  reason: ideal => ideal.batteryScore != null ? `Closest to the ${ideal.batteryScore}/100 battery score that suits how you'll use your laptop.` : `The strongest battery and efficiency of the bunch.`
}, {
  label: "Operating system",
  value: it => it.specs?.operating_system ?? "—",
  winnerIds: (items, ideal) => matchesTarget(items, it => it.specs?.operating_system, ideal.os),
  reason: ideal => `Runs ${ideal.os}, the system you said you preferred.`
}, {
  label: "Screen type",
  value: it => it.specs?.display?.panel_type ?? it.specs?.display?.resolution ?? "—",
  winnerIds: () => [],
  reason: () => ""
}];

// ── Ports & features table ──────────────────────────────────────────────────────

type PortRow = {
  label: string;
  value: (it: LaptopInformation) => string;
  winnerIds: (items: LaptopInformation[]) => string[];
  reason: string;
};
function boolWinners(items: LaptopInformation[], getter: (it: LaptopInformation) => boolean | null | undefined): string[] {
  const haves = items.filter(it => getter(it)).map(getId);
  return haves.length === 0 || haves.length === items.length ? [] : haves;
}
const PORT_ROWS: PortRow[] = [{
  label: "USB-A",
  value: it => {
    const n = it.connectivity?.num_usb_a;
    if (!n) return "—";
    const d = it.connectivity?.usb_a_details ?? [];
    return d.length ? `${n}× (${d.map(x => `${x[0]}× ${x[1]}`).join(", ")})` : `${n}×`;
  },
  winnerIds: items => bestBy(items, it => it.connectivity?.num_usb_a ?? 0, "high"),
  reason: "Has the most USB-A ports of the laptops compared."
}, {
  label: "USB-C",
  value: it => {
    const n = it.connectivity?.num_usb_c;
    if (!n) return "—";
    const d = it.connectivity?.usb_c_details ?? [];
    return d.length ? `${n}× (${d.map(x => `${x[0]}× ${x[1]}`).join(", ")})` : `${n}×`;
  },
  winnerIds: items => bestBy(items, it => it.connectivity?.num_usb_c ?? 0, "high"),
  reason: "Has the most USB-C ports of the laptops compared."
}, {
  label: "HDMI",
  value: it => it.connectivity?.has_hdmi ? it.connectivity.hdmi_details ?? "Yes" : "—",
  winnerIds: items => boolWinners(items, it => it.connectivity?.has_hdmi),
  reason: "Has HDMI output for monitors and external displays."
}, {
  label: "Ethernet",
  value: it => it.connectivity?.has_ethernet ? it.connectivity.ethernet_details ?? "Yes" : "—",
  winnerIds: items => boolWinners(items, it => it.connectivity?.has_ethernet),
  reason: "Has a wired ethernet port for faster, more reliable internet."
}, {
  label: "Audio jack",
  value: it => it.connectivity?.has_audio_jack ? it.connectivity.audio_jack_details ?? "Yes" : "—",
  winnerIds: items => boolWinners(items, it => it.connectivity?.has_audio_jack),
  reason: "Has a 3.5mm audio jack for headphones and speakers."
}, {
  label: "Card reader",
  value: it => it.connectivity?.has_card_reader ? it.connectivity.card_reader_details ?? "Yes" : "—",
  winnerIds: items => boolWinners(items, it => it.connectivity?.has_card_reader),
  reason: "Has a card reader, handy for cameras and media work."
}, {
  label: "Mini DisplayPort",
  value: it => it.connectivity?.has_mini_display_port ? it.connectivity.mini_display_port_details ?? "Yes" : "—",
  winnerIds: items => boolWinners(items, it => it.connectivity?.has_mini_display_port),
  reason: "Has a Mini DisplayPort for external monitor connection."
}, {
  label: "WiFi",
  value: it => it.connectivity?.wifi_version ?? "—",
  winnerIds: () => [],
  reason: ""
}, {
  label: "Bluetooth",
  value: it => it.connectivity?.bluetooth_version ?? "—",
  winnerIds: () => [],
  reason: ""
}];
const FEATURE_ROWS: PortRow[] = [{
  label: "Fingerprint reader",
  value: it => it.features?.has_finger_print_reader ? it.features.finger_print_reader_details ?? "Yes" : "—",
  winnerIds: items => boolWinners(items, it => it.features?.has_finger_print_reader),
  reason: "Has a fingerprint reader for quick, secure sign-in."
}, {
  label: "Webcam",
  value: it => it.features?.has_web_camera ? it.features.web_camera_details ?? "Yes" : "—",
  winnerIds: items => boolWinners(items, it => it.features?.has_web_camera),
  reason: "Has a built-in webcam."
}, {
  label: "Backlit keyboard",
  value: it => it.features?.has_backlit_keyboard ? it.features.backlit_keyboard_details ?? "Yes" : "—",
  winnerIds: items => boolWinners(items, it => it.features?.has_backlit_keyboard),
  reason: "Has a backlit keyboard for typing in low light."
}, {
  label: "Microphone",
  value: it => it.features?.has_microphone ? it.features.microphone_details ?? "Yes" : "—",
  winnerIds: items => boolWinners(items, it => it.features?.has_microphone),
  reason: "Has a built-in microphone."
}, {
  label: "Speakers",
  value: it => it.features?.has_speakers ? it.features.speakers_details ?? "Yes" : "—",
  winnerIds: items => boolWinners(items, it => it.features?.has_speakers),
  reason: "Has built-in speakers."
}];
function PortsTable(t0) {
  const $ = _c(22);
  const {
    title,
    colLabel,
    rows,
    items
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <div className="border-b border-border/60 px-6 py-3"><p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</p></div>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== colLabel) {
    t2 = <th className="sticky left-0 bg-card px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{colLabel}</th>;
    $[2] = colLabel;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== items) {
    t3 = items.map(_temp);
    $[4] = items;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <thead><tr className="border-b border-border/60">{t2}{t3}</tr></thead>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== items || $[10] !== rows) {
    let t6;
    if ($[12] !== items) {
      t6 = (row, r) => {
        const winnerIds = row.winnerIds(items);
        const vals = items.map(it_0 => row.value(it_0));
        const allSame = winnerIds.length === 0 && vals.length > 0 && vals[0] !== "\u2014" && vals.every(v => v === vals[0]);
        return <tr key={row.label} className={r % 2 === 1 ? "bg-surface/60" : ""}><td className="sticky left-0 bg-inherit px-6 py-3 text-[13px] font-medium text-muted-foreground">{row.label}</td>{allSame ? <td colSpan={items.length} className="px-4 py-3"><span className="text-[13px] text-muted-foreground">{vals[0]}{" "}<span className="text-xs italic opacity-70">— same across all options</span></span></td> : items.map(it_1 => {
            const isWin = winnerIds.includes(getId(it_1));
            if (isWin) {
              return <td key={getId(it_1)} className="px-4 py-3"><span className="group relative inline-flex"><span className="inline-flex cursor-help items-center gap-1.5 rounded-lg bg-brand/10 px-2 py-1 text-[13px] font-semibold text-foreground ring-1 ring-brand/20"><span className="text-brand"><IconCheck /></span>{row.value(it_1)}</span>{row.reason && <span className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 w-60 -translate-x-1/2 rounded-xl bg-foreground px-3.5 py-3 text-[11px] leading-relaxed text-background opacity-0 shadow-xl transition-opacity duration-150 group-hover:opacity-100">{row.reason}<span className="absolute left-1/2 top-full -translate-x-1/2 border-[5px] border-transparent border-t-foreground" /></span>}</span></td>;
            }
            return <td key={getId(it_1)} className="px-4 py-3"><span className={`inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-[13px] font-medium ${row.value(it_1) === "\u2014" ? "text-muted-foreground/40" : "text-foreground"}`}>{row.value(it_1)}</span></td>;
          })}</tr>;
      };
      $[12] = items;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    t5 = rows.map(t6);
    $[9] = items;
    $[10] = rows;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[14] !== t5) {
    t6 = <tbody>{t5}</tbody>;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t4 || $[17] !== t6) {
    t7 = <div className="overflow-x-auto"><table className="w-full border-collapse text-sm">{t4}{t6}</table></div>;
    $[16] = t4;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t1 || $[20] !== t7) {
    t8 = <div>{t1}{t7}</div>;
    $[19] = t1;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  return t8;
}

// ── View ────────────────────────────────────────────────────────────────────────
function _temp(it, i) {
  return <th key={getId(it)} className="min-w-[10rem] px-4 py-3 text-left"><span className="flex items-center gap-1.5 text-xs font-bold text-foreground"><span className="size-2 rounded-full" style={{
        backgroundColor: getCompareColor(i)
      }} />{getDisplayName(it)}</span></th>;
}
function averageRadar(it: LaptopInformation): number {
  const vals = Object.values(normaliseToRadarScores(it)) as number[];
  return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
}
export function CompareView(t0) {
  const $ = _c(135);
  const {
    items,
    country,
    answers,
    brands,
    reference,
    idealCpu,
    idealGpu,
    personalised,
    onStartOver,
    startOverLabel: t1,
    onPersonalise
  } = t0;
  const startOverLabel = t1 === undefined ? "Start over" : t1;
  let detailHref;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  let topPick;
  if ($[0] !== answers || $[1] !== brands || $[2] !== country || $[3] !== idealCpu || $[4] !== idealGpu || $[5] !== items || $[6] !== personalised || $[7] !== reference) {
    const sym = priceSymbol(country);
    const apps = appNamesFromAnswers(answers) || "your apps";
    const t19 = idealCpu?.name ?? null;
    const t20 = idealGpu?.name ?? null;
    let t21;
    if ($[27] !== answers || $[28] !== reference || $[29] !== t19 || $[30] !== t20) {
      t21 = buildIdealTargets(reference, answers, t19, t20);
      $[27] = answers;
      $[28] = reference;
      $[29] = t19;
      $[30] = t20;
      $[31] = t21;
    } else {
      t21 = $[31];
    }
    const ideal = t21;
    let t22;
    if ($[32] !== items) {
      t22 = items.map(_temp2);
      $[32] = items;
      $[33] = t22;
    } else {
      t22 = $[33];
    }
    const barSeries = t22;
    let t23;
    if ($[34] !== items) {
      t23 = deriveVerdictLabel(items);
      $[34] = items;
      $[35] = t23;
    } else {
      t23 = $[35];
    }
    const verdicts = t23;
    let t24;
    if ($[36] !== answers || $[37] !== items) {
      t24 = generateSummaryParagraph(items, answers);
      $[36] = answers;
      $[37] = items;
      $[38] = t24;
    } else {
      t24 = $[38];
    }
    const summary = t24;
    let t25;
    if ($[39] !== items) {
      bb0: {
        if (items.length < 2) {
          t25 = null;
          break bb0;
        }
        const withAvg = items.map(_temp3);
        const bestAvg = Math.max(...withAvg.map(_temp4));
        let t26;
        if ($[41] !== bestAvg) {
          t26 = x_0 => x_0.avg === bestAvg;
          $[41] = bestAvg;
          $[42] = t26;
        } else {
          t26 = $[42];
        }
        const winners = withAvg.filter(t26);
        if (winners.length === withAvg.length) {
          t25 = null;
          break bb0;
        }
        t25 = {
          winners: winners.map(_temp5),
          single: winners.length === 1 ? winners[0].it : null
        };
      }
      $[39] = items;
      $[40] = t25;
    } else {
      t25 = $[40];
    }
    const avgData = t25;
    let t26;
    if ($[43] !== country || $[44] !== personalised) {
      t26 = it_1 => `${personalised && getMatchScore(it_1) != null ? "" : "/spec"}/${country}/${toSlug(it_1.laptop.series)}/${it_1.laptop.config_number}`;
      $[43] = country;
      $[44] = personalised;
      $[45] = t26;
    } else {
      t26 = $[45];
    }
    detailHref = t26;
    topPick = avgData?.single ?? items.find(_temp6) ?? [...items].sort(_temp7)[0];
    const topPickMatch = getMatchScore(topPick);
    const gridCols = items.length === 2 ? "sm:grid-cols-2" : items.length === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2 lg:grid-cols-4";
    t11 = "space-y-10";
    if ($[46] !== answers || $[47] !== brands || $[48] !== idealCpu || $[49] !== idealGpu || $[50] !== personalised || $[51] !== reference) {
      t12 = personalised && <div className="plample-rise"><IdealSpecsBanner answers={answers} brands={brands} reference={reference} idealCpu={idealCpu} idealGpu={idealGpu} /></div>;
      $[46] = answers;
      $[47] = brands;
      $[48] = idealCpu;
      $[49] = idealGpu;
      $[50] = personalised;
      $[51] = reference;
      $[52] = t12;
    } else {
      t12 = $[52];
    }
    const t27 = `grid grid-cols-1 gap-5 plample-rise ${gridCols}`;
    const t28 = items.map((it_3, i_0) => {
      const img = getImage(it_3);
      const match = getMatchScore(it_3);
      const price = getPrice(it_3);
      const isTop = personalised && it_3.rank === 1 && match != null;
      return <article key={getId(it_3)} className="flex flex-col overflow-hidden rounded-3xl bg-card ring-1 ring-border/60 shadow-[0_2px_24px_-8px_rgba(0,0,0,0.07)]"><div className="relative aspect-[4/3] overflow-hidden bg-surface">{img ? <img src={img} alt={getDisplayName(it_3)} width={600} height={450} className="size-full object-contain" /> : <div className="size-full bg-surface" />}<span className="absolute left-3 top-3 h-1.5 w-10 rounded-full" style={{
            backgroundColor: getCompareColor(i_0)
          }} />{isTop && <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-brand px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-brand-foreground shadow-sm"><IconStar />Your top pick</span>}</div><div className="flex flex-1 flex-col p-4"><p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{getCompany(it_3)}</p><Link href={detailHref(it_3)} className="mt-0.5 text-sm font-bold leading-snug tracking-tight underline-offset-2 hover:underline">{getDisplayName(it_3)}</Link><div className="mt-3 flex items-center justify-between">{match != null ? <div className="inline-flex items-baseline gap-1"><span className="text-2xl font-bold leading-none tabular-nums text-foreground">{match}</span><span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">match</span></div> : <span />}<span className={`font-semibold text-foreground ${match != null ? "text-sm" : "text-xl"}`}>{price > 0 ? `${sym}${price.toLocaleString()}` : "Price unavailable"}</span></div><span className="mt-3 inline-flex w-fit items-center rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide" style={{
            color: getCompareColor(i_0),
            backgroundColor: "color-mix(in oklab, " + getCompareColor(i_0) + " 12%, transparent)"
          }}>{verdicts[getId(it_3)]}</span></div></article>;
    });
    if ($[53] !== t27 || $[54] !== t28) {
      t13 = <section className={t27}>{t28}</section>;
      $[53] = t27;
      $[54] = t28;
      $[55] = t13;
    } else {
      t13 = $[55];
    }
    if ($[56] !== summary) {
      t14 = summary && <section className="rounded-3xl bg-card p-6 ring-1 ring-border/60 shadow-[0_2px_24px_-8px_rgba(0,0,0,0.07)] sm:p-8 plample-rise"><h2 className="text-sm font-semibold uppercase tracking-widest text-brand">At a glance</h2><p className="mt-3 text-base leading-relaxed text-foreground">{summary}</p></section>;
      $[56] = summary;
      $[57] = t14;
    } else {
      t14 = $[57];
    }
    let t29;
    let t30;
    if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
      t29 = <h2 className="text-sm font-semibold uppercase tracking-widest text-brand">Compare area by area</h2>;
      t30 = <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">Flick through each area to see how the laptops stack up. Longer bars are better.</p>;
      $[58] = t29;
      $[59] = t30;
    } else {
      t29 = $[58];
      t30 = $[59];
    }
    let t31;
    if ($[60] !== barSeries) {
      t31 = <div className="mt-6"><CompareBars series={barSeries} /></div>;
      $[60] = barSeries;
      $[61] = t31;
    } else {
      t31 = $[61];
    }
    let t32;
    if ($[62] !== avgData || $[63] !== detailHref || $[64] !== items) {
      t32 = avgData && <div className="mt-5 flex items-start gap-3 rounded-2xl bg-brand/8 px-4 py-3 ring-1 ring-brand/20"><span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-brand/20 text-brand"><IconStar /></span><p className="text-[13px] leading-relaxed text-foreground">{avgData.winners.reduce((acc, it_4, i_1) => {
            const idx = items.findIndex(x_2 => getId(x_2) === getId(it_4));
            const link = <Link key={getId(it_4)} href={detailHref(it_4)} className="cursor-pointer font-semibold underline-offset-2 hover:underline" style={{
              color: getCompareColor(idx)
            }}>{getDisplayName(it_4)}</Link>;
            if (i_1 === 0) {
              return [link];
            }
            return [...acc, " and ", link];
          }, [])}{" "}{avgData.winners.length === 1 ? "leads " : "lead "} across the board with the highest average score, the strongest all-round choice of the laptops you're comparing.</p></div>;
      $[62] = avgData;
      $[63] = detailHref;
      $[64] = items;
      $[65] = t32;
    } else {
      t32 = $[65];
    }
    if ($[66] !== t31 || $[67] !== t32) {
      t15 = <section className="rounded-3xl bg-card p-6 ring-1 ring-border/60 shadow-[0_2px_24px_-8px_rgba(0,0,0,0.07)] sm:p-8 plample-rise">{t29}{t30}{t31}{t32}</section>;
      $[66] = t31;
      $[67] = t32;
      $[68] = t15;
    } else {
      t15 = $[68];
    }
    let t33;
    if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
      t33 = <h2 className="text-sm font-semibold uppercase tracking-widest text-brand">Spec by spec</h2>;
      $[69] = t33;
    } else {
      t33 = $[69];
    }
    const t34 = personalised ? "The best option for your use case is highlighted in green. Hover it to see why." : "The strongest option on each spec is highlighted in green. Hover it to see why.";
    let t35;
    if ($[70] !== t34) {
      t35 = <div className="border-b border-border/60 px-6 py-5">{t33}<p className="mt-1 text-sm text-muted-foreground">{t34}</p></div>;
      $[70] = t34;
      $[71] = t35;
    } else {
      t35 = $[71];
    }
    let t36;
    if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
      t36 = <th className="sticky left-0 bg-card px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Spec</th>;
      $[72] = t36;
    } else {
      t36 = $[72];
    }
    let t37;
    if ($[73] !== items) {
      t37 = items.map(_temp8);
      $[73] = items;
      $[74] = t37;
    } else {
      t37 = $[74];
    }
    let t38;
    if ($[75] !== t37) {
      t38 = <thead><tr className="border-b border-border/60">{t36}{t37}</tr></thead>;
      $[75] = t37;
      $[76] = t38;
    } else {
      t38 = $[76];
    }
    const t39 = SPEC_ROWS.map((row, r) => {
      const winnerIds = row.winnerIds(items, ideal);
      const reason = row.reason(ideal, sym, apps);
      const vals = items.map(it_6 => row.value(it_6, sym));
      const allSame = winnerIds.length === 0 && vals.length > 0 && vals[0] !== "\u2014" && vals.every(v => v === vals[0]);
      return <tr key={row.label} className={r % 2 === 1 ? "bg-surface/60" : ""}><td className="sticky left-0 bg-inherit px-6 py-3 text-[13px] font-medium text-muted-foreground">{row.label}</td>{allSame ? <td colSpan={items.length} className="px-4 py-3"><span className="text-[13px] text-muted-foreground">{vals[0]}{" "}<span className="text-xs italic opacity-70">— same across all options</span></span></td> : items.map(it_7 => {
          const isWin = winnerIds.includes(getId(it_7));
          if (isWin) {
            return <td key={getId(it_7)} className="px-4 py-3"><span className="group relative inline-flex"><span className="inline-flex cursor-help items-center gap-1.5 rounded-lg bg-brand/10 px-2 py-1 text-[13px] font-semibold text-foreground ring-1 ring-brand/20"><span className="text-brand"><IconCheck /></span>{row.value(it_7, sym)}</span>{reason && <span className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 w-60 -translate-x-1/2 rounded-xl bg-foreground px-3.5 py-3 text-[11px] leading-relaxed text-background opacity-0 shadow-xl transition-opacity duration-150 group-hover:opacity-100">{reason}<span className="absolute left-1/2 top-full -translate-x-1/2 border-[5px] border-transparent border-t-foreground" /></span>}</span></td>;
          }
          return <td key={getId(it_7)} className="px-4 py-3"><span className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-[13px] font-medium text-foreground">{row.value(it_7, sym)}</span></td>;
        })}</tr>;
    });
    let t40;
    if ($[77] !== t39) {
      t40 = <tbody>{t39}</tbody>;
      $[77] = t39;
      $[78] = t40;
    } else {
      t40 = $[78];
    }
    let t41;
    if ($[79] !== t38 || $[80] !== t40) {
      t41 = <div className="overflow-x-auto"><table className="w-full border-collapse text-sm">{t38}{t40}</table></div>;
      $[79] = t38;
      $[80] = t40;
      $[81] = t41;
    } else {
      t41 = $[81];
    }
    if ($[82] !== t35 || $[83] !== t41) {
      t16 = <section className="overflow-hidden rounded-3xl bg-card ring-1 ring-border/60 shadow-[0_2px_24px_-8px_rgba(0,0,0,0.07)] plample-rise">{t35}{t41}</section>;
      $[82] = t35;
      $[83] = t41;
      $[84] = t16;
    } else {
      t16 = $[84];
    }
    let t42;
    if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
      t42 = <div className="border-b border-border/60 px-6 py-5"><h2 className="text-sm font-semibold uppercase tracking-widest text-brand">{"Ports & features"}</h2><p className="mt-1 text-sm text-muted-foreground">What each laptop offers for connectivity and everyday use. Highlighted options have something the others don't.</p></div>;
      $[85] = t42;
    } else {
      t42 = $[85];
    }
    if ($[86] !== items) {
      t17 = <section className="overflow-hidden rounded-3xl bg-card ring-1 ring-border/60 shadow-[0_2px_24px_-8px_rgba(0,0,0,0.07)] plample-rise">{t42}<PortsTable title="Ports & connectivity" colLabel="Port" rows={PORT_ROWS} items={items} /><PortsTable title="Features" colLabel="Feature" rows={FEATURE_ROWS} items={items} /></section>;
      $[86] = items;
      $[87] = t17;
    } else {
      t17 = $[87];
    }
    const t43 = `grid grid-cols-1 gap-5 plample-rise ${items.length >= 3 ? "lg:grid-cols-2" : "sm:grid-cols-2"}`;
    let t44;
    if ($[88] !== items) {
      t44 = items.map(_temp11);
      $[88] = items;
      $[89] = t44;
    } else {
      t44 = $[89];
    }
    if ($[90] !== t43 || $[91] !== t44) {
      t18 = <section className={t43}>{t44}</section>;
      $[90] = t43;
      $[91] = t44;
      $[92] = t18;
    } else {
      t18 = $[92];
    }
    t8 = "rounded-3xl bg-brand/10 p-6 ring-1 ring-brand/20 sm:p-8 plample-rise";
    if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <h2 className="text-xl font-bold tracking-tight sm:text-2xl">Still not sure?</h2>;
      $[93] = t9;
    } else {
      t9 = $[93];
    }
    const t45 = personalised ? "Based on what you told us, here's our honest take." : "Going on the specs alone, here's our honest take. Answer a few questions and we'll weigh them against how you'll actually use it.";
    if ($[94] !== t45) {
      t10 = <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted-foreground">{t45}</p>;
      $[94] = t45;
      $[95] = t10;
    } else {
      t10 = $[95];
    }
    t6 = "mt-5 flex flex-col gap-4 rounded-2xl bg-card p-5 ring-1 ring-border/60 sm:flex-row sm:items-center";
    const t46 = topPickMatch != null && <div className="inline-flex items-baseline gap-1 rounded-2xl bg-brand px-3 py-2 text-brand-foreground"><span className="text-xl font-bold leading-none tabular-nums">{topPickMatch}</span><span className="text-[9px] font-semibold uppercase tracking-wide opacity-80">match</span></div>;
    let t47;
    if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
      t47 = <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-brand">Our pick</p>;
      $[96] = t47;
    } else {
      t47 = $[96];
    }
    const t48 = <div>{t47}<p className="text-base font-bold tracking-tight">{getDisplayName(topPick)}</p></div>;
    if ($[97] !== t46 || $[98] !== t48) {
      t7 = <div className="flex items-center gap-3">{t46}{t48}</div>;
      $[97] = t46;
      $[98] = t48;
      $[99] = t7;
    } else {
      t7 = $[99];
    }
    t2 = "flex-1 text-sm leading-relaxed text-muted-foreground";
    t3 = verdicts[getId(topPick)];
    t4 = ": ";
    t5 = oneLineVerdict(topPick, normaliseToRadarScores(topPick)).toLowerCase();
    $[0] = answers;
    $[1] = brands;
    $[2] = country;
    $[3] = idealCpu;
    $[4] = idealGpu;
    $[5] = items;
    $[6] = personalised;
    $[7] = reference;
    $[8] = detailHref;
    $[9] = t10;
    $[10] = t11;
    $[11] = t12;
    $[12] = t13;
    $[13] = t14;
    $[14] = t15;
    $[15] = t16;
    $[16] = t17;
    $[17] = t18;
    $[18] = t2;
    $[19] = t3;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
    $[26] = topPick;
  } else {
    detailHref = $[8];
    t10 = $[9];
    t11 = $[10];
    t12 = $[11];
    t13 = $[12];
    t14 = $[13];
    t15 = $[14];
    t16 = $[15];
    t17 = $[16];
    t18 = $[17];
    t2 = $[18];
    t3 = $[19];
    t4 = $[20];
    t5 = $[21];
    t6 = $[22];
    t7 = $[23];
    t8 = $[24];
    t9 = $[25];
    topPick = $[26];
  }
  let t19;
  if ($[100] !== t2 || $[101] !== t3 || $[102] !== t4 || $[103] !== t5) {
    t19 = <p className={t2}>{t3}{t4}{t5}</p>;
    $[100] = t2;
    $[101] = t3;
    $[102] = t4;
    $[103] = t5;
    $[104] = t19;
  } else {
    t19 = $[104];
  }
  let t20;
  if ($[105] !== t19 || $[106] !== t6 || $[107] !== t7) {
    t20 = <div className={t6}>{t7}{t19}</div>;
    $[105] = t19;
    $[106] = t6;
    $[107] = t7;
    $[108] = t20;
  } else {
    t20 = $[108];
  }
  const t21 = detailHref(topPick);
  let t22;
  if ($[109] !== t21) {
    t22 = <Link href={t21} className="inline-flex h-11 flex-1 items-center justify-center rounded-full bg-brand px-6 text-sm font-semibold text-brand-foreground shadow-[0_4px_18px_-4px_oklch(0.71_0.14_165_/_0.55)] transition-all hover:opacity-90 active:scale-[0.98]">See full details →</Link>;
    $[109] = t21;
    $[110] = t22;
  } else {
    t22 = $[110];
  }
  let t23;
  if ($[111] !== onPersonalise || $[112] !== onStartOver || $[113] !== personalised || $[114] !== startOverLabel) {
    t23 = personalised ? <button onClick={onStartOver} className="inline-flex h-11 cursor-pointer items-center justify-center rounded-full px-6 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">{startOverLabel}</button> : onPersonalise ? <button onClick={onPersonalise} className="inline-flex h-11 cursor-pointer items-center justify-center rounded-full px-6 text-sm font-medium text-muted-foreground ring-1 ring-border/70 transition-colors hover:text-foreground">Score these against my needs</button> : <Link href="/recommend" className="inline-flex h-11 items-center justify-center rounded-full px-6 text-sm font-medium text-muted-foreground ring-1 ring-border/70 transition-colors hover:text-foreground">Get a personalised pick</Link>;
    $[111] = onPersonalise;
    $[112] = onStartOver;
    $[113] = personalised;
    $[114] = startOverLabel;
    $[115] = t23;
  } else {
    t23 = $[115];
  }
  let t24;
  if ($[116] !== t22 || $[117] !== t23) {
    t24 = <div className="mt-5 flex flex-col gap-3 sm:flex-row">{t22}{t23}</div>;
    $[116] = t22;
    $[117] = t23;
    $[118] = t24;
  } else {
    t24 = $[118];
  }
  let t25;
  if ($[119] !== t10 || $[120] !== t20 || $[121] !== t24 || $[122] !== t8 || $[123] !== t9) {
    t25 = <section className={t8}>{t9}{t10}{t20}{t24}</section>;
    $[119] = t10;
    $[120] = t20;
    $[121] = t24;
    $[122] = t8;
    $[123] = t9;
    $[124] = t25;
  } else {
    t25 = $[124];
  }
  let t26;
  if ($[125] !== t11 || $[126] !== t12 || $[127] !== t13 || $[128] !== t14 || $[129] !== t15 || $[130] !== t16 || $[131] !== t17 || $[132] !== t18 || $[133] !== t25) {
    t26 = <div className={t11}>{t12}{t13}{t14}{t15}{t16}{t17}{t18}{t25}</div>;
    $[125] = t11;
    $[126] = t12;
    $[127] = t13;
    $[128] = t14;
    $[129] = t15;
    $[130] = t16;
    $[131] = t17;
    $[132] = t18;
    $[133] = t25;
    $[134] = t26;
  } else {
    t26 = $[134];
  }
  return t26;
}
function _temp11(it_8, i_3) {
  const radar = normaliseToRadarScores(it_8);
  const {
    strengths,
    weaknesses
  } = deriveStrengthsWeaknesses(it_8, radar);
  return <article key={getId(it_8)} className="flex flex-col rounded-3xl bg-card p-6 ring-1 ring-border/60 shadow-[0_2px_24px_-8px_rgba(0,0,0,0.07)]"><div className="flex items-center gap-2"><span className="size-2.5 rounded-full" style={{
        backgroundColor: getCompareColor(i_3)
      }} /><h3 className="text-base font-bold tracking-tight">{getDisplayName(it_8)}</h3></div><p className="mt-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">What it's great at</p><ul className="mt-2 space-y-2">{strengths.map(_temp9)}</ul><p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.18em]" style={{
      color: AMBER
    }}>Where it falls short</p><ul className="mt-2 space-y-2">{weaknesses.map(_temp10)}</ul><p className="mt-5 border-t border-border/50 pt-4 text-[13px] font-medium leading-relaxed text-muted-foreground">{oneLineVerdict(it_8, radar)}</p></article>;
}
function _temp10(w, idx_1) {
  return <li key={idx_1} className="flex items-start gap-2 text-[13px] leading-relaxed text-foreground"><span className="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full" style={{
      backgroundColor: "color-mix(in oklab, " + AMBER + " 15%, transparent)",
      color: AMBER
    }}><IconWarning /></span>{w}</li>;
}
function _temp9(s, idx_0) {
  return <li key={idx_0} className="flex items-start gap-2 text-[13px] leading-relaxed text-foreground"><span className="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full bg-brand/15 text-brand"><IconCheck /></span>{s}</li>;
}
function _temp8(it_5, i_2) {
  return <th key={getId(it_5)} className="min-w-[10rem] px-4 py-3 text-left"><span className="flex items-center gap-1.5 text-xs font-bold text-foreground"><span className="size-2 rounded-full" style={{
        backgroundColor: getCompareColor(i_2)
      }} />{getDisplayName(it_5)}</span></th>;
}
function _temp7(a, b) {
  return (getMatchScore(b) ?? averageRadar(b)) - (getMatchScore(a) ?? averageRadar(a));
}
function _temp6(it_2) {
  return it_2.rank === 1 && getMatchScore(it_2) != null;
}
function _temp5(x_1) {
  return x_1.it;
}
function _temp4(x) {
  return x.avg;
}
function _temp3(it_0) {
  return {
    it: it_0,
    avg: averageRadar(it_0)
  };
}
function _temp2(it, i) {
  return {
    id: getId(it),
    name: getDisplayName(it),
    color: getCompareColor(i),
    scores: normaliseToRadarScores(it)
  };
}